import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faMobileAlt,
  faCreditCard,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard, EmailGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";


const WithdrawalsPage = () => {
  const [balance, setBalance] = useState(null);
  const [showBalance, setShowBalance] = useState(true);
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedMode, setSelectedMode] = useState(null);
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [senderNames, setSenderNames] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const token = localStorage.getItem("token");
  const idnumber = localStorage.getItem("settle_idnumber");

  useEffect(() => {
    const fetchBalancesAndBanks = async () => {
      if (!token) {
        toast.error("Authentication token not found.");
        return;
      }

      try {
        // Fetch balance
        const balanceResponse = await axios.get(
          `${StandardURL}/iNyumba/admin/househunter/fetchhunterbalance/${idnumber}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const fetchedBalance = balanceResponse.data.response;
        setBalance(fetchedBalance);
        setWithdrawalAmount(fetchedBalance);

        // Fetch banks
        const banksResponse = await axios.get(
          `${StandardURL}/iNyumba/admin/bank/fetch`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setBanks(banksResponse.data.response);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch data.");
      }
    };

    fetchBalancesAndBanks();
  }, [token, idnumber]);

  const handleWithdrawalSubmit = async (e) => {
    e.preventDefault();

    if (!withdrawalAmount || !selectedMode) {
      toast.error("Withdrawal amount and channel are required.");
      return;
    }

    if (withdrawalAmount > balance) {
      toast.error("Withdrawal amount cannot be greater than your balance.");
      return;
    }

    setIsLoading(true);

    try {
      let requestData = {};
      let endpoint = "";

      if (selectedMode === "mobilemoney") {
        if (!phoneNumber || !selectedChannel) {
          toast.error("Phone number and mobile money channel are required.");
          setIsLoading(false);
          return;
        }
        requestData = {
          amount: Number(withdrawalAmount),
          phone_number: phoneNumber,
          channel: selectedChannel,
        };
        endpoint = `${StandardURL}/iNyumba/propertyhuntersettlement/${idnumber}`;
      } else if (selectedMode === "cards") {
        if (!selectedBank || !bankAccount || !senderNames) {
          toast.error("All bank details are required.");
          setIsLoading(false);
          return;
        }
        const bankCode = banks.find(b => b.bankname === selectedBank)?.bankcode;
        if (!bankCode) {
          toast.error("Invalid bank selected.");
          setIsLoading(false);
          return;
        }
        requestData = {
          amount: Number(withdrawalAmount),
          bank_account: bankAccount,
          bank_code: bankCode,
          sendernames: senderNames,
        };
        endpoint = `${StandardURL}/iNyumba/propertyhuntersettlementcards/${idnumber}`;
      }

      const response = await axios.post(endpoint, requestData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.responseCode === 200) {
        if (response.data.response === "B2C processing request") {
          toast.success("Withdrawal request submitted successfully.");
        } else {
          toast.error(response.data.response);
        }
      } else {
        toast.error(response.data.response || "An error occurred during withdrawal.");
      }
      
      setWithdrawalAmount(balance);
      setPhoneNumber("");
      setSelectedChannel(null);
      setSelectedMode(null);
      setSelectedBank("");
      setBankAccount("");
      setSenderNames("");
    } catch (error) {
      console.error("Withdrawal error:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBankSelect = (e) => {
    setSelectedBank(e.target.value);
  };

  const toggleShowBalance = () => {
    setShowBalance(!showBalance);
  };

  const handleModeSelection = (mode) => {
    setSelectedMode(mode);
    setSelectedChannel(null);
    setSelectedBank("");
  };

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-100 p-8">
          {/* Header Section */}
          <div className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-md mb-8">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-3xl font-bold text-gray-800">Settlements</h1>
              <span className="text-sm font-medium text-gray-500">Date: {new Date().toLocaleDateString()}</span>
            </div>

            <div className="flex items-center justify-between bg-blue-50 p-4 rounded-md">
              <h2 className="text-xl font-semibold text-gray-700">Your Balance:</h2>
              <div className="flex items-center space-x-4">
                <p className="text-2xl font-extrabold text-blue-600">
                  {showBalance ? `Ksh ${balance !== null ? balance : "0.00"}` : "Ksh *******"}
                </p>
                <FontAwesomeIcon
                  icon={showBalance ? faEyeSlash : faEye}
                  className="text-xl text-gray-600 cursor-pointer hover:text-blue-500 transition-colors"
                  onClick={toggleShowBalance}
                />
              </div>
            </div>
          </div>
          
          {/* Withdrawal Form */}
          <div className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 border-b pb-2">Initiate a Withdrawal</h2>

            {/* Payment Mode Selection */}
            <div className="flex items-center justify-center space-x-6 mb-8">
              <button
                type="button"
                onClick={() => handleModeSelection("mobilemoney")}
                className={`flex flex-col items-center p-4 rounded-lg transition-all duration-300 transform hover:scale-105 ${
                  selectedMode === "mobilemoney" ? "bg-blue-600 text-white shadow-lg" : "bg-gray-200 text-gray-700"
                }`}
              >
                <FontAwesomeIcon icon={faMobileAlt} className="text-3xl mb-2" />
                <span className="font-semibold">Mobile Money</span>
              </button>
              <button
                type="button"
                onClick={() => handleModeSelection("cards")}
                className={`flex flex-col items-center p-4 rounded-lg transition-all duration-300 transform hover:scale-105 ${
                  selectedMode === "cards" ? "bg-blue-600 text-white shadow-lg" : "bg-gray-200 text-gray-700"
                }`}
              >
                <FontAwesomeIcon icon={faCreditCard} className="text-3xl mb-2" />
                <span className="font-semibold">Bank Transfer</span>
              </button>
            </div>

            <form onSubmit={handleWithdrawalSubmit} className="space-y-6">
              <div>
                <label htmlFor="withdrawalAmount" className="block text-gray-700 font-semibold mb-2">
                  Amount (Ksh)
                </label>
                <input
                  type="number"
                  id="withdrawalAmount"
                  value={withdrawalAmount}
                  onChange={(e) => setWithdrawalAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Mobile Money Options */}
              {selectedMode === "mobilemoney" && (
                <>
                  <div className="space-y-4">
                    <label className="block text-gray-700 font-semibold">Select Channel</label>
                    <div className="flex items-center space-x-4">
                      <button
                        type="button"
                        onClick={() => setSelectedChannel("mpesa")}
                        className={`flex items-center px-6 py-3 rounded-md font-semibold transition-colors duration-200 ${
                          selectedChannel === "mpesa" ? "bg-green-600 text-white" : "bg-gray-200 text-gray-600 hover:bg-green-500 hover:text-white"
                        }`}
                      >
                        MPESA
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedChannel("airtelmoney")}
                        className={`flex items-center px-6 py-3 rounded-md font-semibold transition-colors duration-200 ${
                          selectedChannel === "airtelmoney" ? "bg-red-600 text-white" : "bg-gray-200 text-gray-600 hover:bg-red-500 hover:text-white"
                        }`}
                      >
                        Airtel Money
                      </button>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="phoneNumber" className="block text-gray-700 font-semibold mb-2">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      id="phoneNumber"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="e.g., 2547xxxxxxxx"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </>
              )}

              {/* Card/Bank Transfer Options */}
              {selectedMode === "cards" && (
                <>
                  <div>
                    <label htmlFor="selectedBank" className="block text-gray-700 font-semibold mb-2">
                      Bank
                    </label>
                    <select
                      id="selectedBank"
                      value={selectedBank}
                      onChange={handleBankSelect}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Bank</option>
                      {banks.map((bank) => (
                        <option key={bank.id} value={bank.bankname}>
                          {bank.bankname}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="senderNames" className="block text-gray-700 font-semibold mb-2">
                      Account Holder Name
                    </label>
                    <input
                      type="text"
                      id="senderNames"
                      value={senderNames}
                      onChange={(e) => setSenderNames(e.target.value)}
                      placeholder="e.g., John Doe"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="bankAccount" className="block text-gray-700 font-semibold mb-2">
                      Bank Account Number
                    </label>
                    <input
                      type="text"
                      id="bankAccount"
                      value={bankAccount}
                      onChange={(e) => setBankAccount(e.target.value)}
                      placeholder="Enter bank account number"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </>
              )}

              <button
                type="submit"
                className={`w-full flex justify-center items-center px-4 py-3 rounded-md font-bold text-white transition-colors duration-300 ${
                  isLoading ? "bg-indigo-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"
                }`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Oval height={24} width={24} color="white" ariaLabel="loading" />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faArrowRight} className="mr-2" /> Submit Withdrawal
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default WithdrawalsPage;